import { Box, CircularProgress, Link, Modal, Typography } from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import PropTypes from 'prop-types';
import { useFirebase } from 'react-redux-firebase';
import { useEffect, useState } from 'react';

const bubbleColors = {
  user: '#D3E3FD',
  bot: '#FFFFFE',
  operator: '#FFF3E0',
};

const getBubbleStyles = (type) => {
  const isUser = type === 2;
  const isBot = type === 3;
  const backgroundColor = isUser
    ? bubbleColors.user
    : isBot
    ? bubbleColors.bot
    : bubbleColors.operator;

  return {
    alignSelf: isUser ? 'flex-end' : 'flex-start',
    backgroundColor,
    borderRadius: isUser ? '16px 0px 16px 16px' : '0px 16px 16px 16px',
    position: 'relative',
    maxWidth: '75%',
    m: 0.5,
  };
};

const folders = {
  image: 'images',
  audio: 'voice_records',
  file: 'files',
};

const MessageBubble = ({ message, type, email, contentType }) => {
  const isOperatorMessage = type === 1 && message.startsWith('*[operador]*');
  const cleanedMessage = isOperatorMessage ? message.replace('*[operador]*', '').trim() : message;
  const [mediaUrl, setMediaUrl] = useState(null);
  const firebase = useFirebase();

  useEffect(() => {
    const fetchUrl = async () => {
      if (contentType === 'image' && message) {
        try {
          const ref = firebase.storage().ref(`${folders[contentType]}/${message}`);
          const url = await ref.getDownloadURL();
          setMediaUrl(url);
        } catch (err) {
          console.error('Error fetching media URL:', err);
        }
      }
    };
    fetchUrl();
  }, [contentType, message, firebase]);

  const renderContent = () => {
    if (contentType !== 'chat') {
      if (contentType === 'image') {
        return mediaUrl ? (
          <ImagePreview mediaUrl={mediaUrl} />
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mt: 0.5,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        );
      }

      if (contentType === 'file') {
        return (
          <Link
            href={message}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              fontSize: '0.8125rem',
              color: '#2f4f9f',
              fontWeight: 500,
              mt: 0.5,
              wordBreak: 'break-all',
            }}
          >
            Descargar archivo
          </Link>
        );
      }
    }

    // default: texto
    return <Typography sx={{ fontSize: '0.8125rem' }}>{cleanedMessage}</Typography>;
  };

  return (
    <Box
      sx={{
        ...getBubbleStyles(type),
        p: 1.5,
        maxWidth: '75%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      {isOperatorMessage && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: '#6b5c3d',
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        >
          <SupportAgentIcon fontSize="small" />
          <span>{email}</span>
        </Box>
      )}
      {renderContent()}
    </Box>
  );
};

MessageBubble.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.number.isRequired, // 1 = operador, 2 = usuario, 3 = bot
  contentType: PropTypes.string.isRequired, // 1 = operador, 2 = usuario, 3 = bot
  email: PropTypes.string,
};

export default MessageBubble;

const ImagePreview = ({ mediaUrl }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box
        component="img"
        src={mediaUrl}
        alt="imagen enviada"
        sx={{
          maxWidth: '100%',
          maxHeight: 250,
          borderRadius: 2,
          objectFit: 'cover',
          mt: 0.5,
          cursor: 'pointer',
          transition: 'transform 0.2s',
          '&:hover': {
            transform: 'scale(1.01)',
          },
        }}
        onClick={() => setOpen(true)}
      />

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
            maxWidth: '90%',
            maxHeight: '90%',
          }}
        >
          <img
            src={mediaUrl}
            alt="imagen en grande"
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              maxHeight: '90dvh',
            }}
          />
        </Box>
      </Modal>
    </>
  );
};

ImagePreview.propTypes = {
  mediaUrl: PropTypes.string.isRequired,
};
